/**
 * Rates and Admin.
 *
 * Rates belong to a profile, so a fleet job and a walk-in recalculate the same
 * lines at different money. Admin is where a shop is created — and the country
 * chosen there sets currency, tax naming, units and paper for good (FR48a).
 */

import { useState } from 'react';
import { useSession } from '../app/SessionProvider.js';
import { COUNTRIES } from '../intl/locale.js';
import { LANGUAGES } from '../intl/i18n.js';
import { TENANTS, USERS } from '../fixtures/shop.js';
import type { RateProfile } from '../calc/types.js';

export function Rates({ profile, onChange }: {
  profile: RateProfile;
  onChange: (next: RateProfile) => void;
}) {
  const { t, session } = useSession();
  const country = session!.country;

  const num = (label: string, value: number, set: (n: number) => void) => (
    <label className="field" key={label}>
      <span>{label}</span>
      <input type="number" step="0.01" min="0" value={value}
             onChange={(e) => set(Number(e.target.value))} />
    </label>
  );

  return (
    <section className="screen">
      <h2>{t('Rates')}</h2>
      <p className="muted lede">
        Per profile. Change any of these and open a job — the totals and the
        write-off meter have already moved.
      </p>

      <label className="field wide">
        <span>Profile</span>
        <select>
          <option>Shop — door rates</option>
          <option>Northgate Plumbing — fleet</option>
          <option>Prairie Auto Recon — dealer</option>
        </select>
      </label>

      <h3 className="sub">Labour</h3>
      <div className="grid3">
        {num('Body $/hr', profile.labourRates.body,
          (n) => onChange({ ...profile, labourRates: { ...profile.labourRates, body: n } }))}
        {num('Refinish $/hr', profile.labourRates.refinish,
          (n) => onChange({ ...profile, labourRates: { ...profile.labourRates, refinish: n } }))}
        {num('Frame $/hr', profile.labourRates.frame,
          (n) => onChange({ ...profile, labourRates: { ...profile.labourRates, frame: n } }))}
        {num('Mechanical $/hr', profile.labourRates.mechanical,
          (n) => onChange({ ...profile, labourRates: { ...profile.labourRates, mechanical: n } }))}
      </div>

      <h3 className="sub">Materials and tax</h3>
      <div className="grid3">
        {num('Paint materials $/refinish hr', profile.paintMaterialsPerHour,
          (n) => onChange({ ...profile, paintMaterialsPerHour: n }))}
        {num('Shop materials $/repair hr', profile.shopMaterialsPerHour,
          (n) => onChange({ ...profile, shopMaterialsPerHour: n }))}
      </div>

      {/* More than one tax, because most places have more than one.
          A Canadian shop in BC charges 5% GST *and* 7% PST, and they do not
          always apply to the same things — PST commonly falls on parts and not
          on labour. One hardcoded rate quietly under-billed every such shop. */}
      <h3 className="sub">Taxes</h3>
      <table className="taxtable">
        <thead>
          <tr>
            <th>Name</th><th className="num">Rate %</th>
            <th className="num">Parts</th><th className="num">Labour</th>
            <th className="num">Materials</th><th className="num">Compound</th><th />
          </tr>
        </thead>
        <tbody>
          {profile.taxes.map((tx, i) => {
            const edit = (patch: Partial<typeof tx>) => onChange({
              ...profile,
              taxes: profile.taxes.map((t, j) => (j === i ? { ...t, ...patch } : t)),
            });
            return (
              <tr key={i}>
                <td>
                  <input value={tx.name} aria-label={`Tax ${i + 1} name`}
                         onChange={(e) => edit({ name: e.target.value })} />
                </td>
                <td className="num">
                  <input type="number" step="0.001" min="0" value={tx.rate}
                         aria-label={`Tax ${i + 1} rate`}
                         onChange={(e) => edit({ rate: Number(e.target.value) })} />
                </td>
                <td className="num">
                  <input type="checkbox" checked={tx.onParts}
                         aria-label={`${tx.name} on parts`}
                         onChange={(e) => edit({ onParts: e.target.checked })} />
                </td>
                <td className="num">
                  <input type="checkbox" checked={tx.onLabour}
                         aria-label={`${tx.name} on labour`}
                         onChange={(e) => edit({ onLabour: e.target.checked })} />
                </td>
                <td className="num">
                  <input type="checkbox" checked={tx.onMaterials}
                         aria-label={`${tx.name} on materials`}
                         onChange={(e) => edit({ onMaterials: e.target.checked })} />
                </td>
                <td className="num">
                  <input type="checkbox" checked={Boolean(tx.compound)}
                         aria-label={`${tx.name} compounds`}
                         onChange={(e) => edit({ compound: e.target.checked })} />
                </td>
                <td className="num">
                  {/* Never the last one: a shop with no tax row would silently
                      issue tax-free estimates. */}
                  {profile.taxes.length > 1 && (
                    <button className="btn small" aria-label={`Remove ${tx.name}`}
                            onClick={() => onChange({ ...profile,
                              taxes: profile.taxes.filter((_, j) => j !== i) })}>
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="taxadd">
        <button className="btn" onClick={() => onChange({ ...profile,
          taxes: [...profile.taxes,
            { name: 'PST', rate: 7, onParts: true, onLabour: false,
              onMaterials: true, compound: false }] })}>
          + Add a tax
        </button>
        <span className="muted small">
          Defaults to <b>PST 7%</b> on parts and materials — British Columbia's
          usual case. Change the name, the rate and what it falls on; nothing here
          is specific to one province.
        </span>
      </div>

      <div className="grid3">
        <label className="field">
          <span>Currency — from the shop's country</span>
          <input value={`${country.currencySymbol}  ${country.currencyCode}`} disabled />
        </label>
        <label className="field">
          <span>Tax name — from the shop's country</span>
          <input value={country.taxName} disabled />
        </label>
        <label className="field">
          <span>Distance</span>
          <input value={country.distanceUnit} disabled />
        </label>
      </div>

      <div className="note">
        <b>Currency is not a setting here</b>
        It comes from the shop's country, chosen once when the shop was created. An
        estimate that has been issued keeps the currency it was issued in.
      </div>
    </section>
  );
}

export function Admin() {
  const { t } = useSession();
  const [newCountry, setNewCountry] = useState('AE');
  const c = COUNTRIES[newCountry]!;

  return (
    <section className="screen">
      <h2>{t('Admin')}</h2>
      <p className="muted lede">
        Every shop is a separate tenant with its own data, users and money.
        <b> Country is chosen once, when the shop is created</b> — it sets the currency,
        the tax name and rate, distance units and paper size, and none of those are
        asked again.
      </p>

      <h3 className="sub">Shops on this platform</h3>
      <div className="tablewrap">
        <table>
          <thead>
            <tr><th>Tenant</th><th>Shop</th><th>Country</th><th>Currency</th>
                <th>Tax</th><th className="num">Users</th><th className="num">Jobs</th></tr>
          </thead>
          <tbody>
            {TENANTS.map((tn) => {
              const k = COUNTRIES[tn.country]!;
              return (
                <tr key={tn.id}>
                  <td className="mono">{tn.id}</td>
                  <td><b>{tn.name}</b></td>
                  <td>{k.name}</td>
                  <td className="mono">{k.currencySymbol} {k.currencyCode}</td>
                  <td className="mono">{k.taxName} {k.defaultTaxRate}%</td>
                  <td className="num">{tn.users}</td>
                  <td className="num">{tn.jobs}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h3 className="sub">Create a shop</h3>
      <div className="grid2">
        <label className="field">
          <span>Shop name</span>
          <input defaultValue="Halcyon Collision Centre" />
        </label>
        <label className="field">
          <span>Country — sets everything below</span>
          <select value={newCountry} onChange={(e) => setNewCountry(e.target.value)}>
            {Object.values(COUNTRIES).map((x) => (
              <option key={x.code} value={x.code}>{x.name}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="grid3">
        <label className="field"><span>Currency</span>
          <input value={`${c.currencySymbol}  ${c.currencyCode}`} disabled /></label>
        <label className="field"><span>Tax</span>
          <input value={`${c.taxName} — ${c.defaultTaxRate}%`} disabled /></label>
        <label className="field"><span>Distance</span>
          <input value={c.distanceUnit} disabled /></label>
      </div>
      <div className="grid3">
        <label className="field"><span>Number format</span>
          <input value={(1234.5).toLocaleString(c.locale, { minimumFractionDigits: 2 })} disabled /></label>
        <label className="field"><span>Estimate paper</span>
          <input value={c.paperSize} disabled /></label>
        <label className="field"><span>Default language</span>
          <select defaultValue={c.code === 'AE' ? 'ar' : 'en'}>
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name} — {l.nativeName}{l.isRtl ? '  (right-to-left)' : ''}
              </option>
            ))}
          </select>
        </label>
      </div>
      <button className="btn primary">Create shop &amp; send invite</button>

      <div className="note warn">
        <b>No password is set here</b>
        The owner receives an invitation and chooses their own password on first
        sign-in. Nobody — not an administrator, not support — ever types or sees
        another user's password.
      </div>

      <h3 className="sub">Users</h3>
      <div className="tablewrap">
        <table>
          <thead>
            <tr><th>Email</th><th>Role</th><th>{t('Language')}</th><th>Status</th></tr>
          </thead>
          <tbody>
            {USERS.map((u) => (
              <tr key={u.email}>
                <td className="mono">{u.email}</td>
                <td>{u.role}</td>
                <td>{u.language}</td>
                <td><span className={`flag ${u.status === 'Active' ? 'auto' : 'need'}`}>
                  {u.status}
                </span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="note">
        <b>Language belongs to the person, not the shop</b>
        The shop sets a default; every user overrides it from the picker at any time.
        Two estimators in the same shop can work in different languages on the same job.
      </div>
    </section>
  );
}
